import type { RaRecord } from 'react-admin';
import { dateCell, toCsv, type CsvColumn } from '../../shared/csvExport';
import type { DecisionStatus, FeatureFlag, FlagDecision } from './featureFlagsApi';

/**
 * Decyzja o funkcji za przełącznikiem (decyzja użytkownika 13.10.2026:
 * „wyłącz i włącz to tylko aktualny stan na stagingu; powinno się dać
 * określić, czy zostaje, czy wylatuje, i dodać komentarz decyzji”).
 *
 * Decyzja = co postanowił Marathon i zespół; stan = co jest włączone na tym
 * środowisku. Zapis decyzji przełącznika nie przestawia — ekran tylko
 * podpowiada niespójność. Reguły walidacji są lustrem API
 * (`apps/api/src/feature-flags/feature-flags.ts`, `decisionRefusal`) —
 * rozstrzyga API, tu tylko szybsza odpowiedź w oknie.
 */

export const DECISION_STATUSES: readonly DecisionStatus[] = ['pending', 'accepted', 'rework', 'rejected'];

/** Te same nazwy co w API (`DECISION_LABELS`). */
export const DECISION_LABELS: Record<DecisionStatus, string> = {
  pending: 'do decyzji',
  accepted: 'wchodzi',
  rework: 'do rozbudowy',
  rejected: 'odrzucone',
};

/** Kolor chipu: do decyzji szary, wchodzi zielony, do rozbudowy pomarańczowy, odrzucone czerwony. */
export const DECISION_COLORS: Record<DecisionStatus, 'default' | 'success' | 'warning' | 'error'> = {
  pending: 'default',
  accepted: 'success',
  rework: 'warning',
  rejected: 'error',
};

export const MAX_DECISION_COMMENT_LENGTH = 2000;

const NO_DECISION: FlagDecision = {
  status: 'pending',
  comment: null,
  decidedAt: null,
  decidedById: null,
  decidedByName: null,
};

export const isDecisionStatus = (v: unknown): v is DecisionStatus =>
  typeof v === 'string' && (DECISION_STATUSES as readonly string[]).includes(v);

/** Decyzja przełącznika; brak (starsze API) albo nieznana wartość = „do decyzji”. */
export function decisionOf(flag: Pick<FeatureFlag, 'decision'>): FlagDecision {
  const d = flag.decision;
  return d && isDecisionStatus(d.status) ? d : NO_DECISION;
}

/** Nazwa decyzji z historii (wartość z bazy); `null` = przed pierwszą decyzją. */
export const decisionLabel = (status: string | null): string =>
  status === null ? DECISION_LABELS.pending : isDecisionStatus(status) ? DECISION_LABELS[status] : status;

export type DecisionCounts = Record<DecisionStatus, number>;

export function decisionCounts(flags: readonly FeatureFlag[]): DecisionCounts {
  const counts: DecisionCounts = { pending: 0, accepted: 0, rework: 0, rejected: 0 };
  for (const f of flags) counts[decisionOf(f).status] += 1;
  return counts;
}

/** Licznik nad tabelą: „wchodzi 12 · do rozbudowy 2 · odrzucone 1 · do decyzji 13”. */
export function decisionCountsText(counts: DecisionCounts): string {
  return (['accepted', 'rework', 'rejected', 'pending'] as const)
    .map((s) => `${DECISION_LABELS[s]} ${counts[s]}`)
    .join(' · ');
}

/**
 * Podpowiedź przy niespójności decyzji i stanu na tym środowisku — tylko
 * informacja, nic się samo nie przestawia. `null` = bez uwag.
 */
export function decisionHint(status: DecisionStatus, enabled: boolean): string | null {
  if (status === 'accepted' && !enabled) {
    return 'Wchodzi, a tu wyłączony — na produkcji to normalne do wydania; po wydaniu administrator włącza przełącznik.';
  }
  if ((status === 'rejected' || status === 'rework') && enabled) {
    return `${status === 'rejected' ? 'Odrzucone' : 'Do rozbudowy'}, a tu włączony — na stagingu to tylko informacja; na produkcji przełącznik zostaje wyłączony.`;
  }
  return null;
}

/**
 * Sprawdzenie decyzji w oknie — lustro `decisionRefusal` z API. Komentarz
 * obowiązkowy przy „do rozbudowy” i „odrzucone” oraz przy powrocie do „do
 * decyzji”; ta sama decyzja z tym samym komentarzem to brak zmiany.
 */
export function decisionError(
  current: FlagDecision,
  status: DecisionStatus,
  rawComment: string,
): string | null {
  const comment = rawComment.trim();
  if (comment.length > MAX_DECISION_COMMENT_LENGTH) {
    return `Komentarz decyzji: najwyżej ${MAX_DECISION_COMMENT_LENGTH} znaków`;
  }
  if ((status === 'rework' || status === 'rejected') && comment === '') {
    return status === 'rework'
      ? 'Decyzja „do rozbudowy” wymaga komentarza — napisz, co trzeba rozbudować'
      : 'Decyzja „odrzucone” wymaga komentarza — napisz, dlaczego funkcja odpada';
  }
  if (status === 'pending' && current.status !== 'pending' && comment === '') {
    return 'Powrót do „do decyzji” wymaga powodu w komentarzu';
  }
  if (status === current.status && comment === (current.comment ?? '')) {
    return `Decyzja „${DECISION_LABELS[status]}” już obowiązuje — bez zmian`;
  }
  return null;
}

/** Kolumny eksportu — do maila albo ClickUpa z decyzjami Marathonu. */
export const DECISION_EXPORT_COLUMNS: readonly CsvColumn[] = [
  { header: 'Pozycje prezentacji', value: (r) => ((r.presentationItems as string[] | undefined) ?? []).join(', ') },
  { header: 'Funkcja', value: (r) => r.label },
  { header: 'Klucz', value: (r) => r.key },
  { header: 'Stan na tym środowisku', value: (r) => (r.enabled ? 'włączony' : 'wyłączony') },
  { header: 'Decyzja', value: (r) => DECISION_LABELS[decisionOf(r as FeatureFlag).status] },
  { header: 'Komentarz decyzji', value: (r) => decisionOf(r as FeatureFlag).comment },
  { header: 'Kto', value: (r) => decisionOf(r as FeatureFlag).decidedByName },
  { header: 'Kiedy', value: (r) => dateCell(decisionOf(r as FeatureFlag).decidedAt) },
  { header: 'Opis', value: (r) => r.description },
];

/** Cały plik CSV z decyzjami (z BOM, separator `;` — jak inne eksporty). */
export function decisionsCsv(flags: readonly FeatureFlag[]): string {
  return toCsv(flags as unknown as RaRecord[], DECISION_EXPORT_COLUMNS);
}
